import { Button } from "../../../../../components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../../../../components/ui/form";
import { Input } from "../../../../../components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { toast } from "react-toastify";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { errorLogger } from "@/utils/helper";
import { Card, CardContent } from "@/components/ui/card";
import { useRegisterMerchant } from "@/pages/auth/_misc/mutations";


const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  phone: z.string().min(1, "Phone is required"),
  address: z.string().min(1, "Address is required"),
  email: z.string().email("Invalid email address"),
});

export type CreateAdminDTO = z.infer<typeof formSchema>;

const SignupForm = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const email = searchParams.get("email");
  const navigate = useNavigate();


  const { mutateAsync, isPending } = useRegisterMerchant({
    token: token as string,
  });
  const form = useForm<CreateAdminDTO>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: email as string || "",
      phone: "",
      address: "",
    },
  });
  const onSubmit = async (data: CreateAdminDTO) => {
    try {
      await mutateAsync(
        {
          ...data,
          email: data.email.toLowerCase(),
        },
        {
          onSuccess(res) {
            toast.success(
              "Registration Successful, Check your email for your login credentials"
            );
            navigate("/login");
          },
        }
      );
    } catch (error) {
      errorLogger(error);
    }
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex justify-center items-center">
          <h1 className="text-2xl">Register</h1>
        </div>
        <div className="w-[585px]  p-4">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-normal"> Name</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder=" Name"
                        {...field}
                        className="h-12 shadow-none"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-normal">Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="Enter Email"
                        {...field}
                        className="h-12 shadow-none"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-normal">Phone Number</FormLabel>
                    <FormControl>
                      <div className="relative w-full">
                        <Input
                          type={"tel"}
                          placeholder="Enter Phone Number"
                          className="h-12 pr-10"
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-normal">Address</FormLabel>
                    <FormControl>
                      <div className="relative w-full">
                        <Input
                          placeholder="Enter Address"
                          className="h-12 pr-10"
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="my-4 flex justify-end">
                <Link to="/login" className="text-primary-100">
                  Already have an account ?
                </Link>
              </div>
              <Button
                disabled={isPending}
                className="!mt-8 h-12 w-full bg-primary-500 disabled:cursor-not-allowed disabled:bg-[#B3B3B3]"
                isLoading={isPending}
              >
                Sign Up
              </Button>
            </form>
          </Form>
        </div>
      </CardContent>
    </Card>
  );
};

export default SignupForm;
